/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import moment from 'moment';

import type { TranslatorProps } from 'react-i18next';

import type TypeActionDrawerStore from 'stores/ActionDrawerStore';

import Form from 'forms/_.extend';
import Loading from 'components/Loading/Loading';
import ActionDrawerHeader from 'views/ActionDrawer/components/ActionDrawerHeader/ActionDrawerHeader';

import ExtendRentalForm from './ExtendRentalForm/ExtendRentalForm';
import ExtendRentalSuccess from './ExtendRentalSuccess/ExtendRentalSuccess';
import ExtendRentalError from './ExtendRentalError/ExtendRentalError';

import { isAdmin } from '../../../../../permissions/general.js';

import styles from '../../../ActionDrawer.module.scss';

type TypeProps = {
  t: TranslatorProps,
  actionDrawerStore: TypeActionDrawerStore,
};

@translate()
@inject('actionDrawerStore')
@observer
export default class ExtendRental extends React.Component<*> {
  createForm: Function = () => {
    const { t, actionDrawerStore }: TypeProps = this.props;

    const asset: Object = this.props.formData;

    let currentEndDate: Date = new Date();
    currentEndDate.setDate(currentEndDate.getDate() - 1);

    if (asset.contract && asset.contract.er_end_date) {
      const contractEndDate = moment(asset.contract.er_end_date, 'YYYY-MM-DD').toDate();

      if (contractEndDate > currentEndDate) {
        currentEndDate = contractEndDate;
      }
    }

    const fields: Array<Object> = [
      {
        name: 'extendDetails',
        label: t('general:reasons.when_extend_till'),
        fields: [
          {
            name: 'endDate',
            label: t('general:labels.new_end_date'),
            rules: `required|date|after:${moment(currentEndDate).format('MMMM DD YYYY')}`,
            value: new Date(
              moment(currentEndDate)
                .add(1, 'd')
                .format('YYYY-MM-DD')
            ),
          },
          {
            name: 'reason',
            label: t('general:reasons.reason_for_extending_contract'),
            rules: 'required|string',
            value: '',
            options: [
              {
                id: '1',
                label: t('general:reasons.options.asset_still_required'),
              },
              {
                id: '2',
                label: t('general:reasons.options.owned_asset_unavailable'),
              },
              {
                id: '3',
                label: t('general:reasons.options.owned_asset_requires_repair'),
              },
              {
                id: '4',
                label: t('general:reasons.options.owned_asset_requires_calibration'),
              },
              {
                id: 'other',
                label: t('general:reasons.options.other'),
              },
            ],
          },
          {
            name: 'newPONumber',
            label: t('general:labels.new_po_number'),
          },
        ],
      },
    ];

    const hooks: Object = {
      onSuccess(data: Object) {
        const { reason, endDate, newPONumber }: Object = data.values().extendDetails;

        actionDrawerStore.extendRentalAsset(asset.id, {
          end_date: endDate,
          reason,
          new_po_number: newPONumber,
        });
      },
    };

    return new Form(
      { fields },
      {
        hooks,
        name: 'extendAssetRental',
      }
    );
  };

  render(): React.Node {
    const { t, actionDrawerStore }: TypeProps = this.props;

    const asset: Object = this.props.formData;

    const title: string = isAdmin()
      ? t('general:actions.extend_asset_rental')
      : t('general:actions.extend_asset_rental_request');

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={title} />
          <ExtendRentalError asset={asset} />
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.success}`}>
          <ActionDrawerHeader title={title} />
          <ExtendRentalSuccess response={actionDrawerStore.response} />
        </div>
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={title} />
        <ExtendRentalForm asset={asset} form={this.createForm()} />
      </div>
    );
  }
}
